import React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Tabs, TabsItemOnOrderChange } from "../../components/tabs";
import { Flex } from "@common/components/Flex";
import { waitMs } from "@common/utils/mock";
import { FolderContext } from "@common/dto/folder/context";
import { getTestAttributes } from "@common/utils/tests";
import { getModuleFolders } from "@common/actions/folders";
import { getFolderFields, getTableFieldSubfields } from "@common/actions/formats";
import { FolderType } from "@common/dto/folder/type";
import { MessageBox } from "@common/components/MessageBox";
import { Loading } from "@common/components/loading";

import { Header } from "./components/header";
import { FieldPicker } from "./components/field-picker";
import { DatasheetElementSettings } from "./components/datasheet-element-settings";
import { DatasheetSaveAs } from "./components/datasheet-save-as";
import { Datasheet } from "./components/datasheet";
import { DatasheetFormatManagement } from "../datasheet-format-management";
import { DatasheetFormatManagementMode } from "../datasheet-format-management/datasheet-format-management.types";
import { createDatasheetFolder, deleteDatasheetFolder, getDatasheetFolders } from "../../services/folders";
import { deleteDatasheetFormat, getDatasheetResultFolderFormats, getTdsResultFolderFormats } from "../../services/formats";
import { EditorProps, useEditorController } from "./controllers/main";
import { DatasheetElement } from "@modules/datasheets/types/datasheet-element";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetPrintingSettings } from "../datasheet-printing-settings";

import "./editor.styles.scss";

export const Editor: React.FC<EditorProps> = (props) => {
  const {
    activeContext,
    activeTableContext,
    currentFormatId,
    copiedDatasheetElementsCount,
    handleDatasheetElementSelect,
    handleSelectionReset,
    isDatasheetElementSelected,
    selectedFieldsInFieldPicker,
    handleSelectedFieldsInFieldPickerChange,
    handleDatasheetElementsDrop,
    isFieldAddedToDatasheet,
    datasheetElementsProcessed,
    handleDatasheetElementChange,
    handleSelectionEnd,
    selectedDatasheetElementsCount,
    datasheet,
    currentTabId,
    handleSelectedTabChange,
    handleTabOrderChange,
    folderPickerOpened,
    openFolderPicker,
    closeFolderPicker,
    handleSaveAsSubmit,
    handleSaveSubmit,
    handleGridSettingsChange,
    datasheetFormatManagementMode,
    datasheetFormatManagementInitial,
    closeDatasheetFormatManagement,
    handleDatasheetFormatManagementSubmit,
    setDatasheetFormatManagementMode,
    openNewDatasheet,
    openLoadDatasheet,
    openNewTableDatasheet,
    defaultFieldComponentSettings,
    setDefaultFieldComponentSettings,
    handleFieldComponentSettingsChange,
    currentFieldComponentMetadata,
    handleDatasheetElementsAlignmentChange,
    handleDatasheetElementsSizeChange,
    handleSelectAllDatasheetElements,
    handleDatasheetTextElementsDrop,
    handleDatasheetElementsCopy,
    handleDatasheetElementsPaste,
    gridSettingsMargin,
    pageBreaks,
    handleDatasheetElementsTabOrderChange,
    handleResetTabOrder,
    handleDatasheetElementResize,
    handleDatasheetElementMove,
    canUndo,
    handleDatasheetElementsUndo,
    canRedo,
    handleDatasheetElementsRedo,
    handlePrintDatasheetElements,
    handlePrintingSettingsClose,
    printingSettingsOpened,
    calculatePaperCount,
    handlePrintWithPageSkip,
    handleTabRename,
    pastingOfTabElementsAllowed,
    handleTabAdd,
    handleTabInsert,
    handleTabCopy,
    handleTabPaste,
    handleTabDelete,
    printableDatasheetHolderId,
    handleDatasheetFormatLoadSubmit,
    messageProps,
    closeMessage,
    saveSuccessMessage,
    clearSaveSuccessMessage,
    isSaving,
    defaultTextComponentSettings,
    setDefaultTextComponentSettings,
    currentTextComponentValue,
    handleTextComponentSettingsChange,
    tabEligibleFieldCount,
  } = useEditorController(props);

  const folderTypeId = React.useMemo(
    () => (activeContext === FolderContext.pedigree ? FolderType.pedigree : FolderType.individual),
    [activeContext]
  );

  const folderRootConfig = React.useMemo(
    () =>
      folderTypeId === FolderType.pedigree
        ? { rootFolderName: "Pedigree Data Folders", rootFolderImgUrl: "gif/df_pedigree.gif" }
        : { rootFolderName: "Individual Data Folders", rootFolderImgUrl: "gif/df_individual.gif" },
    [folderTypeId]
  );

  const getFieldFolders = React.useCallback(async () => {
    // For table datasheets, return empty array (no folders shown)
    if (props.tableFieldId != null && props.tableFieldId !== 0) {
      return [];
    }

    // Mirror spreadsheet behavior: load both individual and pedigree trees
    const individualRoot = { rootFolderId: "ind-root", rootFolderName: "Individual Data Folders", rootFolderImgUrl: "gif/df_individual.gif" };
    const pedigreeRoot = { rootFolderId: "ped-root", rootFolderName: "Pedigree Data Folders", rootFolderImgUrl: "gif/df_pedigree.gif" };

    const [individualFolders, pedigreeFolders] = await Promise.all([
      getModuleFolders({
        folderTypeId: FolderType.individual,
        setUp: individualRoot,
      }),
      getModuleFolders({
        folderTypeId: FolderType.pedigree,
        setUp: pedigreeRoot,
        // For Pedigree datasheets, include system fields; for Individual, avoid duplication
        systemFolderConfig: activeContext === FolderContext.pedigree ? { include: true } : { include: false },
      }),
    ]);

    const collapseFoldersRecursive = (folders: any[]): any[] =>
      folders.map((f) => ({
        ...f,
        collapsed: true,
        children: f.children ? collapseFoldersRecursive(f.children) : f.children,
      }));

    const collapsedIndividualFolders = collapseFoldersRecursive(individualFolders);
    const collapsedPedigreeFolders = collapseFoldersRecursive(pedigreeFolders);

    // For Pedigree datasheets, only show Pedigree folders (context 2)
    // For Individual datasheets, show both Individual and Pedigree folders
    const children = activeContext === FolderContext.pedigree 
      ? collapsedPedigreeFolders 
      : [...collapsedIndividualFolders, ...collapsedPedigreeFolders];

    // Wrap both contexts under a single root for cleaner toggle behavior
    return [
      {
        id: -1,
        name: "Data Folders",
        imageURL: "gif/df_individual.gif",
        imageURLSelected: "gif/df_individual.gif",
        parentid: 0,
        secToken: 0,
        system: false,
        context: FolderContext.individual,
        collapsed: true,
        children,
      },
    ];
  }, [activeContext, props.tableFieldId]);

  const getFieldList = React.useCallback(
    async (params: { searchQuery?: string; folderId?: number; context: number }) => {
      // For table datasheets, return subfields of the table field
      if (props.tableFieldId != null && props.tableFieldId !== 0) {
        const contextToUse = activeTableContext??1 ;

        const subfields = await getTableFieldSubfields({
          fieldId: props.tableFieldId,
          context: contextToUse,
        });
        // Filter by search query if provided
        if (params.searchQuery) {
          const query = params.searchQuery.toLowerCase();
          return subfields.filter(field => 
            field.name.toLowerCase().includes(query)
          );
        }
        return subfields;
      }
      // For regular datasheets, use the standard folder fields API
      return getFolderFields({
        searchQuery: params.searchQuery,
        folderId: params.folderId,
        contextId: params.context,
        includeTables: true,
        includePDSFields: Boolean(params.searchQuery),
      });
    },
    [props.tableFieldId, activeTableContext]
  );

  const datasheetElementDndType = "datasheet_editor_element";

  return (
    <div className="DatasheetsEditor">
      {saveSuccessMessage && (
        <div
          className="DatasheetsEditor__toast"
          role="status"
          aria-live="polite"
          onClick={clearSaveSuccessMessage}
        >
          {saveSuccessMessage}
        </div>
      )}
      {isSaving && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 9999 }}>
          <Loading size={25} />
        </div>
      )}
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      <Header
        className="DatasheetsEditor__header"
        context={activeContext}
        tableFieldId={props.tableFieldId}
        currentTabId={currentTabId}
        disabledState={{
          addText: datasheet.gridSettings.showEditTabOrder,
          tabs: datasheet.gridSettings.showEditTabOrder,
          print: datasheet.gridSettings.showEditTabOrder,
          undo: datasheet.gridSettings.showEditTabOrder || !canUndo,
          redo: datasheet.gridSettings.showEditTabOrder || !canRedo,
          datasheetElementCopy: copiedDatasheetElementsCount > 0 ? true : selectedDatasheetElementsCount === 0,
          datasheetElementPaste: copiedDatasheetElementsCount === 0,
          selectAll: datasheet.gridSettings.showEditTabOrder,
          alignment: datasheet.gridSettings.showEditTabOrder,
          tabPaste: !pastingOfTabElementsAllowed,
        }}
        onAddText={handleDatasheetTextElementsDrop}
        onSave={() => {handleSaveSubmit(activeContext)}}
        onSaveAs={openFolderPicker}
        onPrint={handlePrintDatasheetElements}
        onUndo={handleDatasheetElementsUndo}
        onRedo={handleDatasheetElementsRedo}
        onDatasheetElementCopy={handleDatasheetElementsCopy}
        onDatasheetElementPaste={handleDatasheetElementsPaste}
        onSelectAll={handleSelectAllDatasheetElements}
        onLoadDatasheet={openLoadDatasheet}
        onNewDatasheet={openNewDatasheet}
        datasheet={datasheet}
        onDatasheetElementAlignmentChange={handleDatasheetElementsAlignmentChange}
        onDatasheetElementSizeAlignmentChange={handleDatasheetElementsSizeChange}
        onGridSettingsChange={handleGridSettingsChange}
        onResetTabOrder={handleResetTabOrder}
        onTabAdd={handleTabAdd}
        onTabInsert={handleTabInsert}
        onTabCopy={handleTabCopy}
        onTabPaste={handleTabPaste}
        onTabDelete={handleTabDelete}
        onTabRename={handleTabRename}
      />
      {
        printingSettingsOpened && (
          <DatasheetPrintingSettings onClose={handlePrintingSettingsClose} calculatePaperCount={calculatePaperCount} onSubmit={handlePrintWithPageSkip} />
        )
      }
      {datasheetFormatManagementMode && (
        <DatasheetFormatManagement
          onClose={closeDatasheetFormatManagement}
          initialSelectedFormatType={datasheetFormatManagementInitial?.initialSelectedFormatType}
          initialTableField={datasheetFormatManagementInitial?.initialTableField}
          getId={{
            title: getTestAttributes({ id: "lbl-new-data-sheet-wnd-header" }).id,
            folderChooserBtn: getTestAttributes({ id: "fp-new-data-sheet-wnd-folder-picker" }).id,
            formatTypes: getTestAttributes({ id: "new-data-sheet-wnd-format-type-drop-btn" }).id,
            selectedFolderNameHolder: getTestAttributes({ id: "tx-new-data-sheet-wnd-folder" }).id,
            formatName: getTestAttributes({ id: "tx-new-data-sheet-wnd-ds-format-name" }).id,
          }}
          onSubmit={datasheetFormatManagementMode === DatasheetFormatManagementMode.load ? handleDatasheetFormatLoadSubmit : handleDatasheetFormatManagementSubmit}
          getFormats={(folderId: number) =>
            getDatasheetResultFolderFormats({
              folderId,
              context: activeContext,
            }).then((res) =>
              res.map((f) => ({
                id: f.id,
                name: f.name,
              }))
            )
          }
          getFolders={() => getDatasheetFolders(activeContext, false)}
          mode={datasheetFormatManagementMode}
          checkFormatNameExists={() => Promise.resolve(false)}
        />
      )}
      {folderPickerOpened && (() => {
        // Determine if it's TDS based on tableFieldId (following getContextAndTds logic)
        const isTDS = props.tableFieldId != null && props.tableFieldId !== 0;
        // For TDS: use context 1, tds: true (following getContextAndTds logic)
        // For regular DS: use activeContext (1 or 2), tds: false
        const folderContext = isTDS ? 1 : activeContext;
        const tds = isTDS;
        
        return (
          <DatasheetSaveAs
            deleteFormat={async (folderId: number, formatId: number) => {
              await deleteDatasheetFormat(formatId, tds);
            }}
            deleteFolder={async (folderId: number) => {
              await deleteDatasheetFolder({ folderId, tds });
            }}
            createFolder={async (data: { parentid?: number; foldername: string }) => {
              await createDatasheetFolder({
                context: folderContext,
                parentId: data.parentid,
                name: data.foldername,
                tds,
              });
            }}
            getFolders={async () =>
              getDatasheetFolders(folderContext, tds)
            }
            getFormats={async (folderId: number) => {
              if (isTDS) {
                // Use TDS formats API when saving from TDS format
                return getTdsResultFolderFormats({ 
                  folderId, 
                  context: folderContext,
                  tableId: props.tableFieldId! 
                }).then((res) =>
                  res.map((f) => ({
                    id: f.id,
                    name: f.name,
                  }))
                );
              } else {
                return getDatasheetResultFolderFormats({ folderId, context: activeContext }).then((res) =>
                  res.map((f) => ({
                    id: f.id,
                    name: f.name,
                  }))
                );
              }
            }}
            onSubmit={(payload) => handleSaveAsSubmit(payload, activeContext, isTDS)}
            onClose={closeFolderPicker}
          />
        );
      })()}
      <Flex className="DatasheetsEditor__content">
        <DndProvider backend={HTML5Backend} options={{ enableTouchEvents: false, enableMouseEvents: true }}>
          <FieldPicker 
            disabled={datasheet.gridSettings.showEditTabOrder}
            isFieldAdded={isFieldAddedToDatasheet}
            dndType={datasheetElementDndType}
            defaultFolderContext={activeContext as FolderContext}
            selection={{
              items: selectedFieldsInFieldPicker,
              onChange: handleSelectedFieldsInFieldPickerChange,
            }}
            className="DatasheetsEditor__section"
            getFolders={getFieldFolders}
            getFields={getFieldList}
          />
          <div className="DatasheetsEditor__section DatasheetsEditor__datasheet">
            {currentTabId && (
              <Tabs 
                items={datasheet.tabs}
                value={currentTabId}
                onChange={handleSelectedTabChange}
                classes={{
                  tabs: "DatasheetsEditor__datasheet__tabs"
                }}
                onTabOrderChange={handleTabOrderChange as TabsItemOnOrderChange}
              />
            )}
            <Datasheet 
              printableDatasheetHolderId={printableDatasheetHolderId}
              onTabOrderChange={handleDatasheetElementsTabOrderChange}
              pageBreaks={pageBreaks}
              gridSettingsMargin={gridSettingsMargin}
              onDatasheetElementUpdate={handleDatasheetElementChange}
              onDatasheetElementMove={handleDatasheetElementMove}
              onDatasheetElementResize={handleDatasheetElementResize}
              datasheet={datasheet}
              selectedDatasheetElementsCount={selectedDatasheetElementsCount}
              onDatasheetElementSelect={handleDatasheetElementSelect}
              isDatasheetElementSelected={isDatasheetElementSelected}
              onSelectionEnd={handleSelectionEnd}
              onSelectionReset={handleSelectionReset}
              datasheetElements={datasheetElementsProcessed}
              onDatasheetElementsDrop={handleDatasheetElementsDrop}
              datasheetElementDragType={datasheetElementDndType} 
              className="DatasheetsEditor__datasheet__component"
            />
          </div>
        </DndProvider>
        <DatasheetElementSettings
          className="DatasheetsEditor__section"
          textComponentValue={currentTextComponentValue}
          fieldComponentValue={currentFieldComponentMetadata?.fieldComponentValue}
          datasheetElement={currentFieldComponentMetadata?.datasheetElement as DatasheetElement<DatasheetFieldComponentGetDto> | undefined}
          formatId={currentFormatId}
          currentTabId={currentTabId}
          defaultFieldComponentValue={defaultFieldComponentSettings}
          onDefaultFieldComponentChange={setDefaultFieldComponentSettings}
          defaultTextComponentValue={defaultTextComponentSettings}
          onFieldComponentChange={handleFieldComponentSettingsChange}
          onTextComponentChange={currentTextComponentValue ? handleTextComponentSettingsChange : setDefaultTextComponentSettings}
          onNewTableDatasheet={openNewTableDatasheet}
          tabEligibleFieldCount={tabEligibleFieldCount}
          selectedDatasheetElementsCount={selectedDatasheetElementsCount}
        />
      </Flex>
    </div>
  );
};